import { FC, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { CgSpinner } from 'react-icons/cg'

import { BulkJobStatus } from '@/models/operations/types'
import { computeBulkJobProgress, getBulkJobPhaseLabel } from '@/services/operations'

import { OPS_BULK_CLEAR_CACHE_PROGRESS_TITLE, OPS_BULK_REDEPLOY_PROGRESS_TITLE } from './opsUiShared'

type BulkActionProgressModalProps = {
  visible: boolean
  job?: BulkJobStatus
  onDismiss: () => void
  onCloseComplete: () => void
}

const AUTO_CLOSE_DELAY_MS = 600

const BulkActionProgressModal: FC<BulkActionProgressModalProps> = ({
  visible,
  job,
  onDismiss,
  onCloseComplete,
}) => {
  const progress = computeBulkJobProgress(job)
  const isComplete = job?.status === 'completed'
  const phaseLabel = getBulkJobPhaseLabel(job)

  useEffect(() => {
    if (!visible || !isComplete) return
    const timer = setTimeout(onCloseComplete, AUTO_CLOSE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [visible, isComplete, onCloseComplete])

  return (
    <Dialog open={visible} onClose={() => {}} className="relative z-[2000]">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {job?.type === 'clear-cache'
                ? OPS_BULK_CLEAR_CACHE_PROGRESS_TITLE
                : OPS_BULK_REDEPLOY_PROGRESS_TITLE}
            </DialogTitle>
            {!isComplete && (
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700 shrink-0"
                onClick={onDismiss}
              >
                Close
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-3">
            {phaseLabel && (
              <div className="flex items-center gap-2 text-gray-500">
                {!isComplete && <CgSpinner className="animate-spin text-xl shrink-0" />}
                <span className="text-sm">{phaseLabel}</span>
              </div>
            )}

            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 text-right tabular-nums">{progress}%</p>

            {job?.errors?.length ? (
              <ul className="text-xs text-red-600 max-h-32 overflow-y-auto">
                {job.errors.map((err) => (
                  <li key={err.storeId}>
                    {err.storeName}: {err.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export { BulkActionProgressModal }
